import { MiniLineChart, type Series } from '@/components/MiniLineChart';
import { Section } from '@/components/Section';
import { Badge } from '@/components/ui/badge';
import { Dot } from '@/components/ui/dot';
import { Skeleton } from '@/components/ui/skeleton';
import { PaneResizer } from '@/features/data-stream/components/PaneResizer';
import { config } from '@/lib/config';
import { grafanaLokiReady, grafanaPromReady } from '@/lib/grafana/client';
import {
  useHostingSeries,
  useRtmGrafanaSync,
  useServiceLogs,
  useServiceSeries,
} from '@/lib/grafana/queries';
import type { RtmServiceMetric } from '@/lib/mqtt/schemas/services';
import { useDronesStore } from '@/lib/store/drones';
import { useRtmStore } from '@/lib/store/rtm';
import { useUiStore } from '@/lib/store/ui';
import { cn } from '@/lib/utils';
import { useShallow } from 'zustand/shallow';
import { RtmTable } from './RtmTable';
import { useRtmPaneResize } from './useRtmResize';

import type { CSSProperties } from 'react';

// Chart colours match the RTM mockup: green primary, blue secondary.
const C_GREEN = '#1d7a40';
const C_BLUE = '#1a5fa8';

/**
 * The RTM context replaces the map area with three panels: Hosting · RTM
 * Services · Service Graphing. DSS moved to the OI context (ENG-183).
 *
 * On small screens this collapses to a single stack.
 */
export function RtmOverlay() {
  // Live RTM metrics + hosting roll-up from Prometheus (ClickUp 86e1w8ude).
  // No-op in mock mode / when Grafana config is absent.
  useRtmGrafanaSync();

  // User-resizable columns (persisted). Widths drive CSS vars so they only
  // apply at lg+ — below lg the columns stack full-width (responsive).
  const widths = useUiStore((s) => s.rtmPaneWidths);
  const resizeHosting = useRtmPaneResize('hosting');
  const resizeServices = useRtmPaneResize('services');
  const vars = {
    '--rw-hosting': `${widths.hosting}px`,
    '--rw-services': `${widths.services}px`,
  } as CSSProperties;

  // 3 columns: Hosting · RTM Services · Service Graphing. The first two have
  // draggable widths; Service Graphing flexes to fill. Stacks below lg.
  return (
    <div
      style={vars}
      className="absolute inset-0 bg-(--color-surface) overflow-auto lg:overflow-x-auto lg:overflow-y-hidden flex flex-col lg:flex-row"
    >
      <section className="flex flex-col overflow-hidden border-b lg:border-b-0 border-(--color-border) w-full lg:w-(--rw-hosting) lg:min-w-[200px] lg:shrink-0 min-h-[280px] lg:min-h-0">
        <HostingPanel />
      </section>
      <PaneResizer className="hidden lg:block" onMouseDown={resizeHosting} />
      <section className="flex flex-col overflow-hidden border-b lg:border-b-0 border-(--color-border) w-full lg:w-(--rw-services) lg:min-w-[220px] lg:shrink-0 min-h-[280px] lg:min-h-0">
        <AgentTilesRow />
        <div className="flex-1 overflow-hidden">
          <RtmTable />
        </div>
      </section>
      <PaneResizer className="hidden lg:block" onMouseDown={resizeServices} />
      <section className="flex flex-col overflow-hidden w-full lg:flex-1 lg:min-w-[220px] min-h-[280px] lg:min-h-0">
        <ServiceDetailPanel />
      </section>
    </div>
  );
}

/* ---- Hosting panel ---- */
function HostingPanel() {
  const hosting = useRtmStore((s) => s.hosting);
  // Hosting mini charts use a fixed recent window (mockup has no selector here).
  const { data: series, isLoading: seriesLoading } = useHostingSeries('60m');
  // Initial-load shimmer: live mode + Grafana configured, store not yet filled.
  const loading = !config.isMock && grafanaPromReady() && hosting == null;
  const healthTone =
    hosting?.health === 'Healthy' ? 'ok' : hosting?.health === 'Degraded' ? 'warn' : 'crit';

  const memCur = hosting != null ? `${hosting.memory_gib} GiB` : '—';
  const cpuCur = last(series?.cpuMilli) != null ? `${Math.round(last(series?.cpuMilli)!)} mc` : '—';
  const netCur = series?.netTx.length
    ? `${last(series.netTx)?.toFixed(1)} / ${last(series.netRx)?.toFixed(1)} MiB/s`
    : '—';
  const upCur = last(series?.uptimePct) != null ? `${last(series?.uptimePct)!.toFixed(2)}%` : '—';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="flex items-center justify-between px-3 py-2 border-b border-(--color-border) bg-(--color-bg)">
        <div className="text-[0.692em] font-bold uppercase tracking-wider">Hosting Environment</div>
        <Badge variant={hosting ? healthTone : 'muted'}>{hosting?.health ?? '—'}</Badge>
      </header>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        <div className="grid grid-cols-2 gap-1.5">
          {loading ? (
            <>
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
              <StatSkeleton />
            </>
          ) : (
            <>
              <Stat
                label="Pods Up"
                value={hosting != null ? String(hosting.pods_up) : '—'}
                tone="ok"
              />
              <Stat
                label="Pods Down"
                value={hosting != null ? String(hosting.pods_down) : '—'}
                tone={hosting && hosting.pods_down > 0 ? 'crit' : 'ok'}
              />
              <Stat
                label="Memory"
                value={hosting != null ? `${hosting.memory_gib} GiB` : '—'}
                tone="ok"
              />
              <Stat
                label="CPU Used"
                value={hosting != null ? `${hosting.cpu_pct}%` : '—'}
                tone={hosting && hosting.cpu_pct >= 70 ? 'warn' : 'ok'}
              />
            </>
          )}
        </div>
        {/* Mockup §"Hosting Environment" mini charts (L4285-L4320). */}
        <ChartCard
          title="Memory Usage"
          current={memCur}
          loading={seriesLoading}
          series={[{ values: series?.memGiB ?? [], color: C_GREEN }]}
        />
        <ChartCard
          title="CPU per Pod (millicores)"
          current={cpuCur}
          loading={seriesLoading}
          series={[{ values: series?.cpuMilli ?? [], color: C_BLUE }]}
        />
        <ChartCard
          title="Network Tx / Rx"
          current={netCur}
          loading={seriesLoading}
          series={[
            { values: series?.netTx ?? [], color: C_GREEN },
            { values: series?.netRx ?? [], color: C_BLUE },
          ]}
        />
        <ChartCard
          title="Cluster Uptime"
          current={upCur}
          currentClassName="text-(--color-green)"
          loading={seriesLoading}
          series={[{ values: series?.uptimePct ?? [], color: C_GREEN }]}
          min={uptimeFloor(series?.uptimePct)}
          max={100}
        />
        {/* Mockup §"Running Pods" — compact table (Pod | ● | Mem | Mem% | CPU%). */}
        <div className="text-[0.692em] font-bold uppercase tracking-wider text-(--color-text3) mt-1 mb-1">
          Running Pods
        </div>
        {loading && (
          <div className="space-y-1">
            {['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].map((k) => (
              <Skeleton key={k} className="h-4 w-full" />
            ))}
          </div>
        )}
        {!loading && !hosting && (
          <div className="text-[0.769em] text-(--color-text3) text-center py-4">
            Awaiting hosting telemetry…
          </div>
        )}
        {hosting && hosting.pods.length === 0 && (
          <div className="text-[0.769em] text-(--color-text3) text-center py-4">
            No pods reported.
          </div>
        )}
        {hosting && hosting.pods.length > 0 && (
          <table className="w-full text-[0.692em] font-mono border-collapse">
            <thead>
              <tr className="text-[0.615em] uppercase tracking-wider text-(--color-text3)">
                <th className="text-left py-1 font-bold">Pod</th>
                <th className="text-center py-1 font-bold">●</th>
                <th className="text-right py-1 font-bold">Mem</th>
                <th className="text-right py-1 font-bold">Mem%</th>
                <th className="text-right py-1 font-bold">CPU%</th>
              </tr>
            </thead>
            <tbody>
              {hosting.pods.map((p) => {
                const warn = p.memPct >= 70;
                return (
                  <tr key={p.name} className="border-b border-(--color-border-lite)">
                    <td className="py-1 pr-1 text-(--color-text) truncate max-w-[110px]">
                      {p.name}
                    </td>
                    <td className="py-1 text-center">
                      <Dot variant={warn ? 'warn' : 'ok'} size="xs" />
                    </td>
                    <td className="py-1 text-right text-(--color-text2)">
                      {p.memMiB != null ? `${p.memMiB} MiB` : '—'}
                    </td>
                    <td
                      className={cn(
                        'py-1 text-right',
                        warn ? 'text-(--color-amber)' : 'text-(--color-text2)',
                      )}
                    >
                      {p.memPct}%
                    </td>
                    <td className="py-1 text-right text-(--color-text2)">{p.cpuPct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Mockup `.hosting-chart-wrap` — label + current value over a mini chart. */
function ChartCard({
  title,
  current,
  currentClassName,
  series,
  height = 44,
  min,
  max,
  loading = false,
}: {
  title: string;
  current: string;
  currentClassName?: string;
  series: Series[];
  height?: number;
  min?: number;
  max?: number;
  loading?: boolean;
}) {
  const hasData = series.some((s) => s.values.length > 1);
  return (
    <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-2 py-1.5">
      <div className="flex items-center justify-between text-[0.615em] font-semibold uppercase tracking-wider text-(--color-text3)">
        <span>{title}</span>
        <span className={cn('font-mono normal-case', currentClassName ?? 'text-(--color-text2)')}>
          {loading ? '' : current}
        </span>
      </div>
      <div className="mt-1">
        {loading ? (
          <Skeleton style={{ height }} className="w-full" />
        ) : hasData ? (
          <MiniLineChart series={series} height={height} min={min} max={max} />
        ) : (
          <div
            className="flex items-center justify-center text-[0.538em] text-(--color-text3)"
            style={{ height }}
          >
            No data
          </div>
        )}
      </div>
    </div>
  );
}

/** Shimmer placeholder matching a Stat tile's footprint. */
function StatSkeleton() {
  return (
    <div className="rounded-md border border-t-2 border-t-(--color-border) bg-(--color-bg) px-2.5 py-2">
      <Skeleton className="h-4 w-10" />
      <Skeleton className="mt-1.5 h-2 w-12" />
    </div>
  );
}

/** Last finite value of a numeric series, or undefined. */
function last(arr: number[] | undefined): number | undefined {
  if (!arr || arr.length === 0) return undefined;
  return arr[arr.length - 1];
}

/** A tight y-floor for uptime charts so small dips are visible. */
function uptimeFloor(arr: number[] | undefined): number {
  if (!arr || arr.length === 0) return 99;
  const lo = Math.min(...arr.filter((n) => Number.isFinite(n)));
  return Math.max(0, Math.min(99.5, Math.floor(lo - 0.2)));
}

/* ---- Agent tiles row (mockup §"Agent info boxes" L3775-L3786) ----
 * Counts derive from c2.aa_agent_status / gcs_agent_status across the live
 * drone fleet. In live mode (drones store empty until MqttSourceLegacy
 * wires telemetry) both counts naturally read 0. */
function AgentTilesRow() {
  const fleet = useDronesStore(useShallow((s) => Object.values(s.byId)));
  const aaOnline = fleet.filter((d) => d.c2?.aa_agent_status === 'Online').length;
  const gcsOnline = fleet.filter((d) => d.c2?.gcs_agent_status === 'Online').length;
  return (
    <div className="grid grid-cols-2 gap-1.5 p-2 border-b border-(--color-border-lite) shrink-0">
      <AgentTile count={aaOnline} label="Aircraft Agents" />
      <AgentTile count={gcsOnline} label="GCS Agents" />
    </div>
  );
}

function AgentTile({ count, label }: { count: number; label: string }) {
  return (
    <div className="rounded-md border border-t-2 border-t-(--color-green) border-(--color-border) bg-(--color-bg) px-2 py-1.5">
      <div
        className={cn(
          'text-base font-bold leading-none',
          count > 0 ? 'text-(--color-green)' : 'text-(--color-text3)',
        )}
      >
        {count}
      </div>
      <div className="text-[0.538em] font-mono text-(--color-text3) uppercase tracking-wider mt-1">
        {label}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: { label: string; value: string; tone: 'ok' | 'warn' | 'crit' }) {
  // Mockup .hosting-stat / .rtm-detail-stat — always a 2px coloured top border.
  return (
    <div
      className={cn(
        'rounded-md border border-t-2 bg-(--color-bg) px-2.5 py-2',
        tone === 'ok' && 'border-t-(--color-green)',
        tone === 'warn' && 'border-t-(--color-amber)',
        tone === 'crit' && 'border-t-(--color-red)',
      )}
    >
      <div
        className={cn(
          'text-base font-bold leading-none',
          tone === 'ok' && 'text-(--color-green)',
          tone === 'warn' && 'text-(--color-amber)',
          tone === 'crit' && 'text-(--color-red)',
        )}
      >
        {value}
      </div>
      <div className="text-[0.538em] font-mono text-(--color-text3) uppercase tracking-wider mt-1.5">
        {label}
      </div>
    </div>
  );
}

/* ---- Service detail panel ---- */
function ServiceDetailPanel() {
  const selected = useRtmStore((s) => s.selectedName);
  const svc = useRtmStore((s) => (selected ? s.byName[selected] : null));
  const win = useRtmStore((s) => s.detailWindow);
  const setWin = useRtmStore((s) => s.setWindow);
  const windows = ['1m', '5m', '15m', '60m', '24h'] as const;
  const { data: series, isLoading: seriesLoading } = useServiceSeries(selected, win);

  const netCur =
    series && (series.netTx.length || series.netRx.length)
      ? `Tx ${last(series.netTx)?.toFixed(2) ?? '—'} · Rx ${last(series.netRx)?.toFixed(2) ?? '—'}`
      : '—';

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="flex items-center justify-between px-3 py-2 border-b border-(--color-border) bg-(--color-bg)">
        <div className="text-[0.692em] font-bold uppercase tracking-wider">
          {svc?.service_name ?? 'Service Graphing'}
        </div>
        <div className="flex gap-0.5 rounded-full bg-(--color-surface) border border-(--color-border) p-0.5">
          {windows.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWin(w)}
              className={cn(
                'h-5 px-2 rounded-full text-[0.615em] font-semibold',
                win === w
                  ? 'bg-(--color-selected) text-(--color-selected-fg)'
                  : 'text-(--color-text3) hover:text-(--color-text2)',
              )}
            >
              {w}
            </button>
          ))}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-3">
        {!svc && (
          <div className="text-[0.846em] text-(--color-text3) text-center py-6">
            Select a service to view metrics
          </div>
        )}
        {svc && (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat
                label={`Uptime (${win})`}
                value={`${svc.uptime_pct.toFixed(1)}%`}
                tone={svc.uptime_pct > 99 ? 'ok' : svc.uptime_pct > 95 ? 'warn' : 'crit'}
              />
              <Stat
                label="Restarts"
                value={String(svc.restarts)}
                tone={svc.restarts === 0 ? 'ok' : 'warn'}
              />
              <Stat label="Active Items" value={`${svc.active ?? '—'}`} tone="ok" />
            </div>
            {/* Mockup §"Service Graphing" detail charts (L13289-L13291). */}
            <ChartCard
              title="Network Traffic (MiB/s)"
              current={netCur}
              height={48}
              loading={seriesLoading}
              series={[
                { values: series?.netTx ?? [], color: C_GREEN },
                { values: series?.netRx ?? [], color: C_BLUE },
              ]}
            />
            <ChartCard
              title="Message Rate (msg/min)"
              current={`In ${svc.msg_in ?? '—'} · Out ${svc.msg_out ?? '—'}`}
              height={48}
              series={[]}
            />
            <ChartCard
              title="Active Items"
              current={`${svc.active ?? '—'}`}
              height={48}
              series={[]}
            />
            <ServiceLogPanel svc={svc} win={win} />
          </div>
        )}
      </div>
    </div>
  );
}

function LogLine({
  level,
  msg,
  ts,
}: { level: 'info' | 'warn' | 'crit'; msg: string; ts?: number }) {
  return (
    <div
      className={cn(
        'flex gap-2',
        level === 'crit' && 'text-(--color-red)',
        level === 'warn' && 'text-(--color-amber)',
        level === 'info' && 'text-(--color-text3)',
      )}
    >
      {ts != null && (
        <span className="shrink-0 text-(--color-text3) opacity-70">{logTime(ts)}</span>
      )}
      <span className="font-bold uppercase shrink-0">{level}</span>
      <span className="break-all">{msg}</span>
    </div>
  );
}

/** HH:MM:SS (UTC) for a log timestamp in epoch-ms. */
function logTime(ms: number): string {
  const d = new Date(ms);
  return [d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');
}

/* ---- Service log panel (ClickUp 86e1w8ude) ----
 * The panel always renders. In demo (mock) mode it shows the synthetic
 * summary lines; in live mode it shows ONLY real Loki lines — never mock
 * data — falling back to status placeholders when Loki is unconfigured,
 * loading, errored, or empty. */
function ServiceLogPanel({ svc, win }: { svc: RtmServiceMetric; win: string }) {
  const lokiReady = grafanaLokiReady();
  const { data, isPending, isError, error, fetchStatus } = useServiceLogs(
    config.isMock ? null : svc.service_name,
    win,
  );

  return (
    <Section title="Service Log">
      <div className="rounded-md bg-(--color-bg) border border-(--color-border) p-2 space-y-0.5 font-mono text-[0.692em] max-h-64 overflow-auto">
        {config.isMock ? (
          // Demo mode only — synthetic summary lines.
          <>
            <LogLine level="info" msg={`${svc.service_name} startup ok`} />
            <LogLine level="info" msg={`Connected to broker · uptime ${svc.uptime_pct}%`} />
            {svc.restarts > 0 && (
              <LogLine level="warn" msg={`Restarted ${svc.restarts} time(s) in window`} />
            )}
            <LogLine level="info" msg={`rx ${svc.rx} MiB/s · tx ${svc.tx} MiB/s`} />
          </>
        ) : !lokiReady ? (
          <div className="text-(--color-text3)">Logs unavailable — Loki not configured.</div>
        ) : (
          <>
            {isPending && fetchStatus === 'fetching' && (
              <div className="text-(--color-text3)">Loading logs…</div>
            )}
            {isError && (
              <LogLine
                level="crit"
                msg={`Loki query failed: ${(error as Error)?.message ?? 'unknown'}`}
              />
            )}
            {data && data.length === 0 && (
              <div className="text-(--color-text3)">No log lines in the selected window.</div>
            )}
            {data?.map((l, i) => (
              <LogLine key={`${l.ts}-${i}`} level={l.level} msg={l.msg} ts={l.ts} />
            ))}
          </>
        )}
      </div>
    </Section>
  );
}
